import { Box, Flex, GReview, HStack, IconLike, Text } from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { autenticado } from 'src/services/auth'
import {
  createReviewCommentVote,
  deleteReviewCommentVote,
  updateReviewCommentVote,
} from 'src/services/marketplace-api/reviews'

type CommentProps = {
  colorVoltage: string
  date: string
  description: string
  id: number
  liked: boolean | null
  quantityOfLikes: number
  quantityOfDislikes: number
  name: string
  rating: number
}

export const Comment: React.FC<CommentProps> = ({
  colorVoltage,
  date,
  description,
  id,
  liked,
  quantityOfLikes,
  quantityOfDislikes,
  name,
  rating,
}) => {
  const [interacted, setInteracted] = useState<boolean>(false)
  const [likedLocal, setLikedLocal] = useState<boolean | null>(liked)
  const [showReadMoreText, setShowReadMoreText] = useState<boolean>(false)
  const [readMoreNoOfLine, setReadMoreNoOfLine] = useState<number | undefined>()

  const readMoreRef = useRef<HTMLParagraphElement | null>(null)

  const route = useRouter()

  const commomFilledStyles = {
    color: 'var(--chakra-colors-neutral-50)',
    css: {
      svg: {
        fill: 'var(--chakra-colors-primary-500)',
        path: { strokeWidth: '0.0625rem' },
      },
    },
    height: '1.1875rem',
    width: '1.1875rem',
  }

  const commomUnfilledStyles = {
    color: 'var(--chakra-colors-primary-500)',
    height: '0.9rem',
    marginRight: '0.125rem',
    width: '0.9rem',
  }

  const handleInteraction = (isLike: boolean) => {
    if (!autenticado()) return route.push('/login')

    if (likedLocal === null) {
      setLikedLocal(isLike)
      setInteracted(true)
      createReviewCommentVote(id, isLike)
      return
    }

    if (likedLocal === isLike) {
      setLikedLocal(null)
      deleteReviewCommentVote(id)
      return
    }

    updateReviewCommentVote(id, isLike)
    setLikedLocal(isLike)
    setInteracted(true)
  }

  useEffect(() => {
    if (!readMoreRef?.current) return

    const computedStyle = window.getComputedStyle(readMoreRef.current)
    const lineHeight = parseFloat(computedStyle.lineHeight)
    const renderedHeight = readMoreRef.current.clientHeight
    const linesRendered = Math.ceil(renderedHeight / lineHeight)

    if (linesRendered > 3) {
      setShowReadMoreText(true)
      setReadMoreNoOfLine(3)
    }
  }, [readMoreRef])

  const renderThanksForReview = (liked = true) => {
    return (
      <HStack
        backgroundColor="primary.100"
        borderRadius="0.25rem"
        paddingX="0.5rem"
        paddingY="0.375rem"
      >
        <IconLike
          {...commomFilledStyles}
          data-testid={liked ? 'like' : 'dislike'}
          color="primary.100"
          cursor="pointer"
          onClick={() => handleInteraction(liked)}
          {...(liked ? {} : { transform: 'rotate(180deg)' })}
        />
        <Text
          color="primary.500"
          fontSize="0.625rem"
          fontWeight="500"
          lineHeight="0.625rem"
          marginInlineStart="0.125rem !important"
        >
          Obrigado pela avaliação
        </Text>
      </HStack>
    )
  }

  return (
    <Box
      backgroundColor="neutral.50"
      borderRadius="0.375rem"
      paddingX="1.25rem"
      paddingY="1rem"
      width="100%"
    >
      <Flex justifyContent="space-between">
        <Box>
          <Text color="neutral.700" fontSize="1rem" fontWeight="700">
            {name}
          </Text>
          <Text color="neutral.500" fontSize="0.75rem" fontWeight="400">
            {colorVoltage}
          </Text>
        </Box>
        <Box>
          <GReview rating={rating} description={rating.toFixed(1)} />
          <Text
            color="neutral.500"
            fontSize="0.75rem"
            fontWeight="400"
            marginTop="0.25rem"
            textAlign="end"
          >
            {date}
          </Text>
        </Box>
      </Flex>
      <Box marginY="1rem">
        <Text
          data-testid="comment-description"
          color="neutral.700"
          fontSize="0.875rem"
          fontWeight="400"
          lineHeight="1.323rem"
          noOfLines={readMoreNoOfLine}
          ref={readMoreRef}
          dangerouslySetInnerHTML={{
            __html: description.replace(/\n/g, '<br>'),
          }}
        />
        {showReadMoreText && (
          <Text
            width="fit-content"
            cursor="pointer"
            color="primary.500"
            fontSize="0.75rem"
            lineHeight="1.134rem"
            onClick={() =>
              setReadMoreNoOfLine((current) => (current === 3 ? undefined : 3))
            }
          >
            {readMoreNoOfLine ? 'Ler mais' : 'Ler menos'}
          </Text>
        )}
      </Box>
      <HStack height="2rem">
        <Text
          color="neutral.700"
          fontSize={{ base: '0.625rem', md: '0.75rem' }}
          lineHeight="0.75rem"
          marginTop="0.125rem"
        >
          Essa avaliação foi útil?
        </Text>

        {interacted && likedLocal ? (
          renderThanksForReview()
        ) : (
          <Flex alignItems="center" justifyContent="center">
            <IconLike
              cursor="pointer"
              data-testid="like"
              onClick={() => handleInteraction(true)}
              {...(likedLocal && !interacted
                ? commomFilledStyles
                : commomUnfilledStyles)}
            />
            <Text
              color="neutral.600"
              fontSize={{ base: '0.625rem', md: '0.75rem' }}
              lineHeight="0.75rem"
              marginTop="0.1875rem"
            >
              {`(${quantityOfLikes})`}
            </Text>
          </Flex>
        )}

        {interacted && likedLocal === false ? (
          renderThanksForReview(false)
        ) : (
          <Flex alignItems="center" justifyContent="center">
            <IconLike
              cursor="pointer"
              data-testid="dislike"
              onClick={() => handleInteraction(false)}
              transform="rotate(180deg)"
              {...(likedLocal === false && !interacted
                ? commomFilledStyles
                : commomUnfilledStyles)}
            />
            <Text
              color="neutral.600"
              fontSize={{ base: '0.625rem', md: '0.75rem' }}
              lineHeight="0.75rem"
              marginTop="0.1875rem"
            >
              {`(${quantityOfDislikes})`}
            </Text>
          </Flex>
        )}
      </HStack>
    </Box>
  )
}
